import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

function BeerProduction({ theme }) {
    const { t } = useLanguage();
    const [productions, setProductions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        insumo_ids: [],
        product_id: '',
        start_date: '',
        finish_date: '',
        quantity: '',
        units: 'L'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('start_date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [insumosList, setInsumosList] = useState([]);
    const [productsList, setProductsList] = useState([]);

    const fetchProductions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/admin/beer-production`);
            if (!response.ok) throw new Error(t('failedToFetchProductions'));
            const data = await response.json();
            setProductions(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    const fetchInsumosList = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/insumos-list`);
            if (!response.ok) throw new Error('Failed to fetch insumos list');
            const data = await response.json();
            setInsumosList(data);
        } catch (err) {
            console.error('Error fetching insumos list:', err);
        }
    };

    const fetchProductsList = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/products-list`);
            if (!response.ok) throw new Error('Failed to fetch products list');
            const data = await response.json();
            setProductsList(data);
        } catch (err) {
            console.error('Error fetching products list:', err);
        }
    };

    useEffect(() => {
        fetchProductions();
        fetchInsumosList();
        fetchProductsList();
    }, [fetchProductions]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = `${BACKEND_URL}/api/admin/beer-production`;
            const method = editingItem ? 'PUT' : 'POST';

            // Convert empty quantity to 0
            const submitData = {
                ...formData,
                quantity: formData.quantity === '' ? 0 : formData.quantity
            };

            const payload = editingItem
                ? {
                    ...submitData,
                    old_insumo_product_ids: editingItem.insumos.map(ins => ins.id)
                  }
                : submitData;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || t('failedToSaveProduction'));
            }

            await fetchProductions();
            resetForm();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            insumo_ids: item.insumos.map(ins => ins.insumo_id),
            product_id: item.product_id,
            start_date: item.start_date ? item.start_date.split('T')[0] : '',
            finish_date: item.finish_date ? item.finish_date.split('T')[0] : '',
            quantity: item.quantity,
            units: item.units
        });
        setShowAddForm(true);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(t('confirmDeleteProduction'))) return;

        try {
            const insumo_product_ids = item.insumos.map(ins => ins.id);
            const response = await fetch(`${BACKEND_URL}/api/admin/beer-production`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ insumo_product_ids })
            });

            if (!response.ok) throw new Error(t('failedToDeleteProduction'));

            await fetchProductions();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const resetForm = () => {
        setFormData({
            insumo_ids: [],
            product_id: '',
            start_date: '',
            finish_date: '',
            quantity: '',
            units: 'L'
        });
        setEditingItem(null);
        setShowAddForm(false);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const toggleInsumo = (insumoId) => {
        setFormData(prev => {
            const insumo_ids = prev.insumo_ids.includes(insumoId)
                ? prev.insumo_ids.filter(id => id !== insumoId)
                : [...prev.insumo_ids, insumoId];
            return { ...prev, insumo_ids };
        });
    };

    // Filter productions
    const filteredProductions = productions.filter(prod => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            prod.product_name?.toLowerCase().includes(search) ||
            prod.insumos.some(ins => ins.insumo_name?.toLowerCase().includes(search)) ||
            prod.units?.toLowerCase().includes(search)
        );
    });

    // Sort productions
    const sortedProductions = [...filteredProductions].sort((a, b) => {
        let aVal, bVal;

        switch (sortField) {
            case 'product_name':
                aVal = a.product_name || '';
                bVal = b.product_name || '';
                break;
            case 'insumo_name':
                // Sort by the first insumo name alphabetically
                const aInsumos = a.insumos.map(ins => ins.insumo_name).sort();
                const bInsumos = b.insumos.map(ins => ins.insumo_name).sort();
                aVal = aInsumos[0] || '';
                bVal = bInsumos[0] || '';
                break;
            case 'start_date':
            case 'finish_date':
                aVal = a[sortField] ? new Date(a[sortField]).getTime() : 0;
                bVal = b[sortField] ? new Date(b[sortField]).getTime() : 0;
                break;
            case 'quantity':
                aVal = parseFloat(a.quantity) || 0;
                bVal = parseFloat(b.quantity) || 0;
                break;
            case 'duration':
                const aDuration = a.finish_date
                    ? Math.ceil((new Date(a.finish_date) - new Date(a.start_date)) / (1000 * 60 * 60 * 24))
                    : 0;
                const bDuration = b.finish_date
                    ? Math.ceil((new Date(b.finish_date) - new Date(b.start_date)) / (1000 * 60 * 60 * 24))
                    : 0;
                aVal = aDuration;
                bVal = bDuration;
                break;
            case 'cost_per_unit':
                const aTotalCost = a.insumos.reduce((sum, ins) => {
                    const price = parseFloat(ins.insumo_price) || 0;
                    const iva = parseFloat(ins.insumo_iva) || 0;
                    return sum + price + iva;
                }, 0);
                const bTotalCost = b.insumos.reduce((sum, ins) => {
                    const price = parseFloat(ins.insumo_price) || 0;
                    const iva = parseFloat(ins.insumo_iva) || 0;
                    return sum + price + iva;
                }, 0);
                aVal = aTotalCost / (parseFloat(a.quantity) || 1);
                bVal = bTotalCost / (parseFloat(b.quantity) || 1);
                break;
            default:
                aVal = a[sortField] || '';
                bVal = b[sortField] || '';
        }

        if (typeof aVal === 'string') {
            return sortDirection === 'asc'
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        }

        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>{t('loadingProductions')}</div>;
    if (error) return <div style={{ padding: '2rem', color: 'red' }}>{error}</div>;

    return (
        <div style={{
            padding: '1.5rem',
            backgroundColor: theme?.colors?.background || '#f5f5f5',
            minHeight: '100vh'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                gap: '1rem'
            }}>
                <h2 style={{
                    margin: 0,
                    color: theme?.colors?.text || '#000',
                    fontSize: '1.75rem'
                }}>
                    {t('beerProductionRuns')}
                </h2>
                <button
                    onClick={() => setShowAddForm(true)}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: theme?.colors?.success || '#4caf50',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 8,
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                    }}
                >
                    + {t('addProductionRun')}
                </button>
            </div>

            {/* Search Bar */}
            <div style={{ marginBottom: '1.5rem' }}>
                <input
                    type="text"
                    placeholder={t('search') + '...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        borderRadius: theme?.borderRadius || 4,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                    }}
                />
            </div>

            {/* Add/Edit Form Modal */}
            {showAddForm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        padding: '2rem',
                        borderRadius: theme?.borderRadius || 8,
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{ marginTop: 0, color: theme?.colors?.text || '#000' }}>
                            {editingItem ? t('editProductionRun') : t('addProductionRun')}
                        </h3>

                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                    {t('productBeer')}:
                                </label>
                                <select
                                    value={formData.product_id}
                                    onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: `1px solid ${theme.colors.primary}40`
                                    }}
                                >
                                    <option value="">{t('selectProduct')}</option>
                                    {productsList.map(product => (
                                        <option key={product.id} value={product.id}>
                                            {product.name}{product.active === 0 ? ' (Inactive)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                    {t('insumoInvoice')} ({t('selectMultiple')}):
                                </label>
                                <div style={{
                                    border: `1px solid ${theme.colors.primary}40`,
                                    borderRadius: theme.borderRadius || 4,
                                    padding: '0.5rem',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}>
                                    {insumosList.map(insumo => (
                                        <label
                                            key={insumo.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '0.5rem',
                                                cursor: 'pointer',
                                                borderRadius: 4,
                                                backgroundColor: formData.insumo_ids.includes(insumo.id)
                                                    ? `${theme.colors.primary}20`
                                                    : 'transparent'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.insumo_ids.includes(insumo.id)}
                                                onChange={() => toggleInsumo(insumo.id)}
                                                style={{ marginRight: '0.5rem' }}
                                            />
                                            <span>
                                                {insumo.name} - {new Date(insumo.purchase_date).toLocaleDateString()}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                    {t('startDate')}:
                                </label>
                                <input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: `1px solid ${theme.colors.primary}40`
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                    {t('finishDate')} ({t('optional')}):
                                </label>
                                <input
                                    type="date"
                                    value={formData.finish_date}
                                    onChange={(e) => setFormData({...formData, finish_date: e.target.value})}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: `1px solid ${theme.colors.primary}40`
                                    }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        {t('quantity')} ({t('optional')}):
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                                        min="0"
                                        placeholder="0"
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            borderRadius: theme.borderRadius || 4,
                                            border: `1px solid ${theme.colors.primary}40`
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        {t('units')}:
                                    </label>
                                    <select
                                        value={formData.units}
                                        onChange={(e) => setFormData({...formData, units: e.target.value})}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            borderRadius: theme.borderRadius || 4,
                                            border: `1px solid ${theme.colors.primary}40`
                                        }}
                                    >
                                        <option value="L">L</option>
                                        <option value="mL">mL</option>
                                        <option value="gal">gal</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.border || '#ccc',
                                        color: theme?.colors?.text || '#000',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 8,
                                        cursor: 'pointer',
                                        fontSize: '1rem'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!formData.insumo_ids.length || !formData.product_id || !formData.start_date || !formData.units}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: (!formData.insumo_ids.length || !formData.product_id || !formData.start_date || !formData.units)
                                            ? '#ccc'
                                            : (theme?.colors?.success || '#4caf50'),
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 8,
                                        cursor: (!formData.insumo_ids.length || !formData.product_id || !formData.start_date || !formData.units)
                                            ? 'not-allowed'
                                            : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        opacity: (!formData.insumo_ids.length || !formData.product_id || !formData.start_date || !formData.units)
                                            ? 0.6
                                            : 1
                                    }}
                                >
                                    {editingItem ? t('update') : t('add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Productions Table */}
            {sortedProductions.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: theme?.colors?.textSecondary || '#666'
                }}>
                    {t('noProductionRunsFound')}
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.9rem'
                    }}>
                        <thead>
                            <tr style={{ borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}` }}>
                                <th
                                    onClick={() => handleSort('product_name')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('productBeer')} {sortField === 'product_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('insumo_name')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('insumoInvoice')} {sortField === 'insumo_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('start_date')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('startDate')} {sortField === 'start_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('finish_date')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('finishDate')} {sortField === 'finish_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('duration')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('duration')} {sortField === 'duration' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('quantity')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'right',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('quantity')} {sortField === 'quantity' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('cost_per_unit')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'right',
                                        color: theme?.colors?.primary || '#1976d2',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('costPerUnit')} {sortField === 'cost_per_unit' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th style={{
                                    padding: '0.75rem',
                                    textAlign: 'center',
                                    color: theme?.colors?.primary || '#1976d2'
                                }}>
                                    {t('actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedProductions.map((production, index) => {
                                const totalCost = production.insumos.reduce((sum, insumo) => {
                                    const price = parseFloat(insumo.insumo_price) || 0;
                                    const iva = parseFloat(insumo.insumo_iva) || 0;
                                    return sum + price + iva;
                                }, 0);
                                const costPerUnit = totalCost / (parseFloat(production.quantity) || 1);
                                const duration = production.finish_date
                                    ? Math.ceil((new Date(production.finish_date) - new Date(production.start_date)) / (1000 * 60 * 60 * 24))
                                    : null;

                                return (
                                    <tr
                                        key={`${production.product_id}_${production.start_date}`}
                                        style={{
                                            borderBottom: `1px solid ${theme?.colors?.border || '#eee'}`,
                                            backgroundColor: index % 2 === 0
                                                ? theme?.colors?.surface || '#fff'
                                                : theme?.colors?.background || '#f9f9f9'
                                        }}
                                    >
                                        <td style={{ padding: '1rem', color: theme?.colors?.text || '#000' }}>
                                            {production.product_name}
                                        </td>
                                        <td style={{ padding: '1rem', color: theme?.colors?.text || '#000' }}>
                                            {production.insumos.map((insumo, idx) => (
                                                <div key={idx} style={{ marginBottom: idx < production.insumos.length - 1 ? '0.25rem' : 0 }}>
                                                    {insumo.insumo_name}
                                                </div>
                                            ))}
                                        </td>
                                        <td style={{ padding: '1rem', color: theme?.colors?.text || '#000' }}>
                                            {new Date(production.start_date).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: '1rem', color: theme?.colors?.text || '#000' }}>
                                            {production.finish_date
                                                ? new Date(production.finish_date).toLocaleDateString()
                                                : <span style={{ fontStyle: 'italic', color: theme?.colors?.textSecondary || '#666' }}>
                                                    {t('productionInProgress')}
                                                </span>
                                            }
                                        </td>
                                        <td style={{ padding: '1rem', color: theme?.colors?.text || '#000' }}>
                                            {duration !== null ? `${duration} ${t('daysLabel')}` : '-'}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'right', color: theme?.colors?.text || '#000' }}>
                                            {parseFloat(production.quantity).toFixed(2)} {production.units}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'right', color: theme?.colors?.text || '#000' }}>
                                            ${costPerUnit.toFixed(2)}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleEdit(production)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    marginRight: '0.5rem',
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {t('edit')}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(production)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: theme?.colors?.error || '#f44336',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {t('delete')}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default BeerProduction;
